import React, { useState } from 'react';
import { Button, ButtonGroup, Grid2, Tooltip, useMediaQuery, useTheme } from '@mui/material';
import { useGetCommissionGroups } from '@openapi/commission-groups/commission-groups';
import { useGetShow } from '@openapi/shows/shows';
import useNotify from '../../hooks/useNotify';
import { TABLE_ROW_COUNT_DEFAULT } from '../../helpers/constants';
import PageWithTitle from '../../components/atom/PageWithTitle';
import CommissionGroupsTable from './components/CommissionGroupsTable';
import SearchInput from '../../components/atom/SearchInput';
import AddIcon from '@mui/icons-material/Add';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useCapabilities } from '../../hooks/useCapabilities';

const CommissionGroupsPage = () => {
    const { t } = useTranslation();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const { can, CAPABILITIES } = useCapabilities();
    const canManage = can(CAPABILITIES.COMMISSION_GROUPS_MANAGE);
    const { notifyError } = useNotify();
    const { showId } = useParams();
    const [{ limit, page }, setParams] = useState({ limit: TABLE_ROW_COUNT_DEFAULT, page: 0 });
    const [search, setSearch] = useState('');
    const navigate = useNavigate();

    const {
        data,
        isLoading: loading,
        refetch: reloadData,
    } = useGetCommissionGroups(Number(showId), {
        query: {
            onError: notifyError,
        },
    });

    const { data: show, isLoading: loadingShow } = useGetShow(Number(showId), {
        query: {
            onError: notifyError,
        },
    });

    // Client-side search filter
    const filteredGroups = data?.commissionGroups?.filter(
        (group) => !search || group.name?.toLowerCase().includes(search.toLowerCase()),
    );

    // Client-side pagination
    const paginatedGroups = filteredGroups?.slice(page * limit, (page + 1) * limit);

    return (
        <PageWithTitle
            title={`${t('commissionGroups.title')} (${loadingShow ? '' : show?.title || ''})`}
            inlineHeadline
            headline={[
                <Grid2
                    container
                    direction="row"
                    justifyContent="flex-end"
                    alignItems="center"
                    wrap="nowrap"
                    key="search-and-buttons"
                >
                    {!isMobile && (
                        <Grid2 sx={{ mr: 2 }}>
                            <SearchInput
                                search={search}
                                setSearch={setSearch}
                                setParams={setParams}
                                limit={limit}
                            />
                        </Grid2>
                    )}
                    {canManage && (
                        <Grid2 sx={{ flexShrink: 0, ml: 2 }}>
                            <ButtonGroup variant="contained">
                                <Tooltip title={t('common.add')}>
                                    <Button
                                        onClick={() => {
                                            navigate(`/shows/${showId}/commissiongroups/new`);
                                        }}
                                    >
                                        <AddIcon />
                                    </Button>
                                </Tooltip>
                            </ButtonGroup>
                        </Grid2>
                    )}
                </Grid2>,
            ]}
        >
            {isMobile && (
                <Grid2 size={{ xs: 12 }}>
                    <SearchInput
                        search={search}
                        setSearch={setSearch}
                        setParams={setParams}
                        limit={limit}
                        fullWidth
                    />
                </Grid2>
            )}
            <CommissionGroupsTable
                rows={paginatedGroups}
                limit={limit}
                page={page}
                setParams={setParams}
                total={filteredGroups?.length || 0}
                loading={loading}
                reloadData={reloadData}
                showId={showId}
            />
        </PageWithTitle>
    );
};

export default CommissionGroupsPage;
